
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Edit, Trash2, Plus, Search, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LearningModule } from '@/types/learning';

export const AdminModuleList = () => {
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('learning_modules')
        .select('*')
        .order('title');

      if (error) throw error;
      
      // Map database fields to our LearningModule type
      const formattedModules: LearningModule[] = (data || []).map(item => ({
        id: item.id,
        title: item.title,
        description: item.description || '',
        difficulty: item.difficulty || '',
        content: item.content,
        created_at: item.created_at,
        updated_at: item.updated_at,
        // Set default values for required fields in our type that might not be in DB
        duration: '', 
        level: item.difficulty || '' // Use difficulty as level if available
      }));
      
      setModules(formattedModules);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load modules. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!moduleToDelete) return;
    
    try {
      const { error } = await supabase
        .from('learning_modules')
        .delete()
        .eq('id', moduleToDelete);
      
      if (error) throw error;
      
      toast({
        title: 'Module deleted',
        description: 'The module has been successfully deleted.',
      });
      
      // Remove the deleted module from state
      setModules(modules.filter(module => module.id !== moduleToDelete));
    } catch (error) {
      console.error('Error deleting module:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete module. Please try again.',
      });
    } finally {
      setModuleToDelete(null);
    }
  };

  const handleCreateModule = () => {
    navigate('/admin/modules/new');
  };

  const filteredModules = modules.filter(module => 
    module.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-2xl font-bold">Learning Modules</CardTitle>
          <Button onClick={handleCreateModule}>
            <Plus className="mr-2 h-4 w-4" />
            Create Module
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search modules..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredModules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'No modules match your search' : 'No modules found. Create your first module!'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredModules.map((module) => (
                    <TableRow key={module.id}>
                      <TableCell className="font-medium">{module.title}</TableCell>
                      <TableCell>{module.difficulty || 'Unspecified'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => navigate(`/module/${module.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => navigate(`/admin/modules/${module.id}`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setModuleToDelete(module.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!moduleToDelete} onOpenChange={(open) => !open && setModuleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the module and remove it from any learning paths it belongs to.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteModule} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
